import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { SuggestionsGrid } from './SuggestionsGrid';
import { sendMessageToChatbot, useChatState } from '../../utils/client/messageUtils';

function getIsMobileViewport() {
    return typeof window !== 'undefined' ? window.innerWidth <= 600 : false;
}

export function SuggestionsBox() {
    const chatState = useChatState();
    const { suggestions, isLoading } = chatState;
    const [currentPage, setCurrentPage] = useState(0);
    const [isMobile, setIsMobile] = useState(getIsMobileViewport);

    const itemsPerRow = isMobile ? 1 : 2;
    const rows = 2;
    const itemsPerPage = itemsPerRow * rows;

    const totalPages = useMemo(
        () => Math.max(1, Math.ceil(suggestions.length / itemsPerPage)),
        [itemsPerPage, suggestions.length]
    );

    useEffect(() => {
        const handleResize = () => setIsMobile(getIsMobileViewport());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setCurrentPage(0);
    }, [suggestions]);

    useEffect(() => {
        setCurrentPage((current) => Math.min(current, totalPages - 1));
    }, [totalPages]);

    function getPaginatedSuggestions() {
        const start = currentPage * itemsPerPage;
        const end = start + itemsPerPage;
        return suggestions.slice(start, end);
    }

    function handlePreviousPage() {
        if (currentPage > 0) setCurrentPage((current) => current - 1);
    }

    function handleNextPage() {
        if (currentPage < totalPages - 1) setCurrentPage((current) => current + 1);
    }

    async function onChooseSuggestion(suggestion) {
        if (isLoading) return;
        
        try {
            await sendMessageToChatbot(suggestion, chatState);
        } catch (error) {
            console.error("Failed to send suggested message:", error);
        }
    }

    if (suggestions.length === 0) return <Box />;

    const navButtonSx = {
        height: 40,
        width: 40,
        backgroundColor: 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(148, 163, 184, 0.28)',
        color: 'rgba(15, 23, 42, 0.75)',
        backdropFilter: 'blur(8px)',
        boxShadow: '0 10px 22px rgba(15, 23, 42, 0.06)',
        '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.94)',
        },
        '&.Mui-disabled': {
            opacity: 0.45,
            color: 'rgba(15, 23, 42, 0.35)',
        },
    };

    return (
        <Box position='relative'>
            {totalPages <= 1 && (
                <SuggestionsGrid
                    onChooseSuggestion={onChooseSuggestion}
                    suggestionsToShow={getPaginatedSuggestions()}
                    itemsPerRow={itemsPerRow}
                    currentPage={currentPage}
                />
            )}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mx: 2, alignItems: 'end' }}>
                    <IconButton aria-label='Previous suggestions' onClick={handlePreviousPage} disabled={currentPage === 0} sx={navButtonSx}>
                        <ArrowBackIcon />
                    </IconButton>
                    <SuggestionsGrid
                        onChooseSuggestion={onChooseSuggestion}
                        suggestionsToShow={getPaginatedSuggestions()}
                        itemsPerRow={itemsPerRow}
                        totalCount={suggestions.length}
                        currentPage={currentPage}
                    />
                    <IconButton aria-label='Next suggestions' onClick={handleNextPage} disabled={currentPage === totalPages - 1} sx={navButtonSx}>
                        <ArrowForwardIcon />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
}